import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { WrapperComponent } from './layout/wrapper/wrapper.component';
import { PermissionKey } from './shared/enum/permission-key.enum';
import { AuthorizationGuard } from './core/guard/authorization.guard';
import { AuthGuard } from './core/guard/auth.guard';
import { PublicGuard } from './core/guard/public.guard';
import { SystemUserGuard } from './core/guard/system-user.guard';
import { TenantGuard } from './core/guard/tenant.guard';
import { ShoppingStoreGuard } from './core/guard/shopping-store.guard';

export const routes: Routes = [
    {
        path: '',
        component: WrapperComponent,
        canActivate: [AuthGuard],
        canActivateChild: [AuthorizationGuard],
        children: [
            {
                path: '',
                loadChildren: () =>
                    import('./dashboard/dashboard.module').then((m) => m.DashboardModule),
            },
            {
                path: 'user',
                data: {
                    permission: {
                        shop: PermissionKey.STAFF_AND_ROLES,
                        seldit: PermissionKey.PLATFORM_PEOPLE,
                    },
                },
                loadChildren: () => import('./user/user.module').then((m) => m.UserModule),
            },
            {
                path: 'role',
                data: {
                    permission: {
                        shop: PermissionKey.STAFF_AND_ROLES,
                        seldit: PermissionKey.PLATFORM_PEOPLE,
                    },
                },
                loadChildren: () => import('./role/role.module').then((m) => m.RoleModule),
            },
            {
                path: 'contact-enquiry',
                data: { permission: PermissionKey.ENQUIRIES },
                loadChildren: () =>
                    import('./contact-enquiry/contact-enquiry.module').then(
                        (m) => m.ContactEnquiryModule,
                    ),
                canActivate: [TenantGuard],
            },
            {
                path: 'brand',
                data: { permission: PermissionKey.PRODUCTS },
                loadChildren: () => import('./brand/brand.module').then((m) => m.BrandModule),
                canActivate: [TenantGuard, ShoppingStoreGuard],
            },
            {
                path: 'tenant',
                data: {
                    permission: {
                        shop: PermissionKey.SETTINGS_STORE,
                        seldit: PermissionKey.PLATFORM_TENANTS,
                    },
                },
                loadChildren: () => import('./tenant/tenant.module').then((m) => m.TenantModule),
            },
            {
                path: 'currency',
                data: { permission: PermissionKey.PLATFORM_REFERENCE_DATA },
                loadChildren: () =>
                    import('./currency/currency.module').then((m) => m.CurrencyModule),
                canActivate: [SystemUserGuard],
            },
            {
                path: 'shipping-method',
                data: { permission: PermissionKey.PLATFORM_REFERENCE_DATA },
                loadChildren: () =>
                    import('./shipping-method/shipping-method.module').then(
                        (m) => m.ShippingMethodModule,
                    ),
                canActivate: [SystemUserGuard],
            },
            {
                path: 'payment-gateway',
                data: { permission: PermissionKey.PLATFORM_REFERENCE_DATA },
                loadChildren: () =>
                    import('./payment-gateway/payment-gateway.module').then(
                        (m) => m.PaymentGatewayModule,
                    ),
                canActivate: [SystemUserGuard],
            },
            {
                path: 'category',
                data: { permission: PermissionKey.PRODUCTS },
                loadChildren: () =>
                    import('./category/category.module').then((m) => m.CategoryModule),
                canActivate: [TenantGuard, ShoppingStoreGuard],
            },
            {
                path: 'tax',
                data: { permission: PermissionKey.PLATFORM_REFERENCE_DATA },
                loadChildren: () => import('./tax/tax.module').then((m) => m.TaxModule),
                canActivate: [SystemUserGuard],
            },
            {
                path: 'manage-list-of-value',
                data: { permission: PermissionKey.PLATFORM_REFERENCE_DATA },
                loadChildren: () =>
                    import('./manage-list-of-value/manage-list-of-value.module').then(
                        (m) => m.ManageListOfValuesModule,
                    ),
                canActivate: [SystemUserGuard],
            },
            {
                path: 'location',
                data: { permission: PermissionKey.PLATFORM_REFERENCE_DATA },
                loadChildren: () =>
                    import('./location/location.module').then((m) => m.LocationModule),
                canActivate: [SystemUserGuard],
            },
            {
                path: 'menu',
                data: { permission: PermissionKey.ONLINE_STORE },
                loadChildren: () => import('./menu/menu.module').then((m) => m.MenuModule),
                canActivate: [TenantGuard],
            },
            {
                path: 'page',
                data: { permission: PermissionKey.ONLINE_STORE },
                loadChildren: () => import('./page/page.module').then((m) => m.PageModule),
                canActivate: [TenantGuard],
            },
            {
                path: 'block',
                data: { permission: PermissionKey.ONLINE_STORE },
                loadChildren: () => import('./block/block.module').then((m) => m.BlockModule),
                canActivate: [TenantGuard],
            },
            {
                path: 'product',
                data: { permission: PermissionKey.PRODUCTS },
                loadChildren: () => import('./product/product.module').then((m) => m.ProductModule),
                canActivate: [TenantGuard, ShoppingStoreGuard],
            },
            {
                path: 'reviews',
                data: { permission: PermissionKey.REVIEWS },
                loadChildren: () => import('./review/review.module').then((m) => m.ReviewModule),
                canActivate: [TenantGuard, ShoppingStoreGuard],
            },
            {
                path: 'coupon',
                data: { permission: PermissionKey.MARKETING },
                loadChildren: () => import('./coupon/coupon.module').then((m) => m.CouponModule),
                canActivate: [TenantGuard, ShoppingStoreGuard],
            },
            {
                path: 'discount',
                data: { permission: PermissionKey.MARKETING },
                loadChildren: () =>
                    import('./discount/discount.module').then((m) => m.DiscountModule),
                canActivate: [TenantGuard, ShoppingStoreGuard],
            },
            {
                path: 'order',
                data: { permission: PermissionKey.ORDERS },
                loadChildren: () => import('./order/order.module').then((m) => m.OrderModule),
                canActivate: [TenantGuard, ShoppingStoreGuard],
            },
            {
                path: 'customer',
                data: { permission: PermissionKey.CUSTOMERS },
                loadChildren: () =>
                    import('./customer/customer.module').then((m) => m.CustomerModule),
                canActivate: [TenantGuard, ShoppingStoreGuard],
            },
            {
                path: 'flash-news',
                data: { permission: PermissionKey.MARKETING },
                loadChildren: () =>
                    import('./flash-news/flash-news.module').then((m) => m.FlashNewsModule),
                canActivate: [TenantGuard],
            },
            {
                path: 'abandoned-cart',
                data: { permission: PermissionKey.ORDERS },
                loadChildren: () => import('./cart/cart.module').then((m) => m.CartModule),
                canActivate: [TenantGuard, ShoppingStoreGuard],
            },
            {
                path: 'forms',
                data: { permission: PermissionKey.ONLINE_STORE },
                loadChildren: () => import('./forms/forms.module').then((m) => m.FormsModule),
                canActivate: [TenantGuard],
            },
            {
                path: 'courier',
                data: { permission: PermissionKey.FULFILMENT },
                loadChildren: () => import('./courier/courier.module').then((m) => m.CourierModule),
                canActivate: [TenantGuard, ShoppingStoreGuard],
            },
            {
                path: 'subscription',
                loadChildren: () =>
                    import('./subscription-plans/subscription-plans.module').then(
                        (m) => m.SubscriptionPlansModule,
                    ),
                canActivate: [TenantGuard],
            },
            {
                path: 'theme',
                data: { permission: PermissionKey.PLATFORM_THEMES },
                loadChildren: () => import('./theme/theme.module').then((m) => m.ThemeModule),
                canActivate: [SystemUserGuard],
            },
        ],
    },
    {
        path: 'settings',
        loadChildren: () => import('./setting/setting.module').then((m) => m.SettingModule),
        canActivate: [AuthGuard],
        canActivateChild: [AuthorizationGuard],
    },
    {
        path: 'auth',
        loadChildren: () => import('./auth/auth.module').then((m) => m.AuthModule),
        canActivate: [PublicGuard],
    },
    {
        path: 'onboarding',
        loadChildren: () =>
            import('./tenant/onboarding/tenant-onboarding.module').then(
                (m) => m.TenantOnboardingModule,
            ),
    },
    {
        path: 'static',
        loadChildren: () =>
            import('./static-pages/static-pages.module').then((m) => m.StaticPagesModule),
    },

    { path: '**', redirectTo: '/static/404' },
];

@NgModule({
    imports: [RouterModule.forRoot(routes)],
    exports: [RouterModule],
})
export class AppRoutingModule {}
